import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import NavBar from '../../common/NavBar';
import PageHeader from '../../common/PageHeader';
import { useTranslation } from 'react-i18next';
import TextInput from '../../common/TextInput/TextInput';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import MultiSelectCheckbox from '../../common/MultiSelectCheckbox/MultiSelectCheckbox';
import Callout from '../Callout';
import CircularProgress from '@mui/material/CircularProgress';
import { useSearchParams } from 'react-router-dom';
import {
  SuppressionFormData,
  SuppressionFormErrors,
  SuppressionReason,
  MarketingChannel
} from './types';
import { useSuppressionContent } from './useSuppressionContent';
import { RenewalsApi } from '../../../api/RenewalsApi';
import {
  allMarketingChannels,
  buildSuppressionSubmissionPayload
} from './helper';
import { useAppSelector } from '../../../app-state/Hooks';
import { CountryIsoCodes } from '../../common/Constants';

const SuppressionPage = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { isLoading, data } = useSuppressionContent();
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);

  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignid');

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [errors, setErrors] = useState<SuppressionFormErrors>({});
  const [formData, setFormData] = useState<SuppressionFormData>({
    firstName: '',
    surname: '',
    email: '',
    telephone: '',
    addressLine1: '',
    postcode: '',
    vehicleRegistration: '',
    vin: '',
    suppressionReason: '',
    marketingChannels: []
  });

  const pageTitle = t('page-title.suppression');
  const formTitle = data?.title || t('suppression.form-title');
  const formDescription = t('suppression.form-description');

  const firstNameLabel = t('personal-details.first-name-label');
  const surnameLabel = t('personal-details.surname-label');
  const emailLabel = t('personal-details.email-address-label');
  const telephoneLabel = t('personal-details.phone-number-label');
  const addressLine1Label = t('personal-details.address1-label');
  const postcodeLabel = t('personal-details.postcode-label');
  const vehicleRegistrationLabel = t('general.vehicle-registration');
  const vinLabel = t('general.vin');
  const countryLabel = t('personal-details.country-label');

  const marketingChannelsLabel = t('suppression.marketing-channels-label');
  const marketingChannelLabel = t('suppression.marketing-channel-label');
  const allMarketingLabel = t('suppression.all-marketing-label');

  const successMessage = t('suppression.submission-success-message');
  const informationTextBox =
    data?.infoBoxText || t('suppression.information-text');

  const postcodeHelperText = t('suppression.postcode-helper-text');
  const vehicleRegistrationHelperText = t(
    'suppression.vehicle-registration-helper-text'
  );

  const countryDisplayName = countryCode
    ? (new Intl.DisplayNames([countryCode, i18n.language, CountryIsoCodes.GB], {
        type: 'region'
      }).of(countryCode.toUpperCase()) ?? countryCode)
    : '';

  const suppressionReasonTranslationMap = {
    [SuppressionReason.NotInterested]: t('suppression.reason.not-interested'),
    [SuppressionReason.AlreadyRenewed]: t('suppression.reason.already-renewed'),
    [SuppressionReason.PurchasedElsewhere]: t(
      'suppression.reason.purchased-elsewhere'
    ),
    [SuppressionReason.NoLongerOwnVehicle]: t(
      'suppression.reason.no-longer-own-vehicle'
    )
  };

  const marketingChannelsTranslation = {
    [MarketingChannel.AllMarketing]: allMarketingLabel,
    [MarketingChannel.DirectMail]: t('general.direct-mail'),
    [MarketingChannel.Email]: t('general.email'),
    [MarketingChannel.SMS]: t('general.sms'),
    [MarketingChannel.Telephone]: t('general.telephone')
  };

  const marketingChannels = allMarketingChannels.map((c) => ({
    value: c,
    label: marketingChannelsTranslation[c]
  }));

  const suppressionReasons = [
    SuppressionReason.NotInterested,
    SuppressionReason.AlreadyRenewed,
    SuppressionReason.PurchasedElsewhere,
    SuppressionReason.NoLongerOwnVehicle
  ].map((key) => ({
    value: key,
    text: suppressionReasonTranslationMap[key]
  }));

  const handleInputChange = (
    e: React.FormEvent<HTMLInputElement>,
    field: keyof SuppressionFormData
  ) => {
    setFormData({
      ...formData,
      [field]: e.currentTarget.value.trim()
    });
  };

  const handleAddressLineChange = (e: React.FormEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      addressLine1: e.currentTarget.value
    });
  };

  const handleInputLeave = (e: React.FormEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      addressLine1: e.currentTarget.value.trim()
    });
  };

  const handleReasonChange = (value: string) => {
    setFormData({
      ...formData,
      suppressionReason: value
    });
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors: SuppressionFormErrors = {};

    if (formData.email && !CommonValidation.isValidEmail(formData.email)) {
      isValid = false;
      newErrors.email = t(
        'personal-details.email-invalid-personal-details-validation'
      );
    }

    if (!formData.surname) {
      isValid = false;
      newErrors.surname = t('validation.required-field', {
        fieldName: surnameLabel
      });
    }

    if (!formData.postcode) {
      isValid = false;
      newErrors.postcode = t('validation.required-field', {
        fieldName: postcodeLabel
      });
    }

    if (!formData.vehicleRegistration && !formData.vin) {
      isValid = false;
      const vinOrVrnError = t('validation.at-least-one-required', {
        fields: `${vehicleRegistrationLabel}, ${vinLabel}`
      });
      newErrors.vehicleRegistration = vinOrVrnError;
      newErrors.vin = vinOrVrnError;
    }

    if (
      formData.marketingChannels?.some(
        (v) => v === MarketingChannel.SMS || v === MarketingChannel.Telephone
      ) &&
      !formData.telephone
    ) {
      isValid = false;
      newErrors.telephone = t('validation.required-field', {
        fieldName: telephoneLabel
      });
    }

    if (
      formData.marketingChannels?.includes(MarketingChannel.Email) &&
      !formData.email
    ) {
      isValid = false;
      newErrors.email = t('validation.required-field', {
        fieldName: emailLabel
      });
    }

    if (
      formData.marketingChannels?.includes(MarketingChannel.DirectMail) &&
      !formData.addressLine1
    ) {
      isValid = false;
      newErrors.addressLine1 = t('validation.required-field', {
        fieldName: addressLine1Label
      });
    }

    if (!formData.marketingChannels?.length) {
      isValid = false;
      newErrors.marketingChannels = t('validation.required-field', {
        fieldName: marketingChannelLabel
      });
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleMarketingChannelsChange = (marketingOptions: string[]) => {
    setFormData({
      ...formData,
      marketingChannels: marketingOptions
    });
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const controller = new AbortController();

      const suppressionDetails = buildSuppressionSubmissionPayload(
        formData,
        campaignId,
        countryCode?.toUpperCase()
      );

      const successCallback = () => {
        setIsFormSubmitted(true);
      };

      const failureCallback = () => {
        console.error('CreateFromSuppression failed');
      };

      new RenewalsApi().createFromSuppression(
        suppressionDetails,
        successCallback,
        failureCallback,
        controller.signal
      );
    }
  };

  if (isLoading) {
    return (
      <div className='loading-container'>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div className='flex-column main-column'>
        <div className='main-content'>
          <NavBar />
          <div className='flex-row'>
            <div className='page-container'>
              <div className='page-header-line' />
              {isFormSubmitted ? (
                <h2>
                  <output id='form-status' aria-live='polite'>
                    {successMessage}
                  </output>
                </h2>
              ) : (
                <>
                  <PageHeader title={formTitle} description={formDescription} />
                  <div className='form'>
                    <div className='flex-row'>
                      <div className='inline-control'>
                        <div className='control-label'>{firstNameLabel}</div>
                        <div className='control'>
                          <TextInput
                            placeholder={firstNameLabel}
                            name='firstName'
                            className='input-text w250'
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              handleInputChange(e, 'firstName')
                            }
                            value={formData.firstName}
                            readOnly={isFormSubmitted}
                          />
                        </div>
                      </div>

                      <div className='inline-control'>
                        <div className='control-label'>{surnameLabel}*</div>
                        <div className='control'>
                          <TextInput
                            placeholder={surnameLabel}
                            name='surname'
                            className='input-text w250'
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              handleInputChange(e, 'surname')
                            }
                            value={formData.surname}
                            error={errors.surname}
                            readOnly={isFormSubmitted}
                          />
                        </div>
                      </div>
                    </div>

                    <div className='flex-row'>
                      <div className='inline-control'>
                        <div className='control-label'>{emailLabel}</div>
                        <div className='control'>
                          <TextInput
                            placeholder={emailLabel}
                            name='email'
                            className='input-text w250'
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              handleInputChange(e, 'email')
                            }
                            value={formData.email}
                            error={errors.email}
                            readOnly={isFormSubmitted}
                          />
                        </div>
                      </div>

                      <div className='inline-control'>
                        <div className='control-label'>{telephoneLabel}</div>
                        <div className='control'>
                          <TextInput
                            placeholder={telephoneLabel}
                            name='telephone'
                            className='input-text w250'
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              handleInputChange(e, 'telephone')
                            }
                            value={formData.telephone}
                            error={errors.telephone}
                            readOnly={isFormSubmitted}
                          />
                        </div>
                      </div>
                    </div>

                    <div className='flex-row'>
                      <div className='inline-control'>
                        <div className='control-label'>{countryLabel}</div>
                        <div className='control'>
                          <TextInput
                            placeholder={countryLabel}
                            name='country'
                            className='input-text w250'
                            value={countryDisplayName}
                            readOnly
                          />
                        </div>
                      </div>

                      <div className='inline-control'>
                        <div className='control-label'>{addressLine1Label}</div>
                        <div className='control'>
                          <TextInput
                            placeholder={addressLine1Label}
                            name='addressLine1'
                            className='input-text w250'
                            onChange={handleAddressLineChange}
                            onLeave={handleInputLeave}
                            value={formData.addressLine1}
                            error={errors.addressLine1}
                            readOnly={isFormSubmitted}
                          />
                        </div>
                      </div>
                    </div>

                    <div className='inline-control'>
                      <div className='control-label'>
                        {postcodeLabel}*
                        <span className='info-text m02-left'>
                          {postcodeHelperText}
                        </span>
                      </div>
                      <div className='control'>
                        <TextInput
                          placeholder={postcodeLabel}
                          name='postcode'
                          className='input-text w250'
                          onChange={(e: React.FormEvent<HTMLInputElement>) =>
                            handleInputChange(e, 'postcode')
                          }
                          value={formData.postcode}
                          error={errors.postcode}
                          readOnly={isFormSubmitted}
                        />
                      </div>
                    </div>

                    <div className='inline-control'>
                      <div className='control-label'>
                        {vehicleRegistrationLabel}
                        <span className='info-text m02-left'>
                          {vehicleRegistrationHelperText}
                        </span>
                      </div>
                      <div className='control'>
                        <TextInput
                          placeholder={vehicleRegistrationLabel}
                          name='vehicleRegistration'
                          className='input-text w250'
                          onChange={(e: React.FormEvent<HTMLInputElement>) =>
                            handleInputChange(e, 'vehicleRegistration')
                          }
                          value={formData.vehicleRegistration}
                          error={errors.vehicleRegistration}
                          readOnly={isFormSubmitted}
                        />
                      </div>
                    </div>

                    <div className='inline-control'>
                      <div className='control-label'>{vinLabel}</div>
                      <div className='control'>
                        <TextInput
                          placeholder={vinLabel}
                          name='vin'
                          className='input-text w250'
                          onChange={(e: React.FormEvent<HTMLInputElement>) =>
                            handleInputChange(e, 'vin')
                          }
                          value={formData.vin}
                          error={errors.vin}
                          readOnly={isFormSubmitted}
                        />
                      </div>
                    </div>
                  </div>
                  <RadioOptions
                    selectedOption={formData.suppressionReason ?? ''}
                    optionsList={suppressionReasons}
                    name='suppressionReason'
                    errorMessage={errors.suppressionReason}
                    setSelectedOption={handleReasonChange}
                    vertical
                    disabled={isFormSubmitted}
                  />
                  <MultiSelectCheckbox
                    options={marketingChannels}
                    value={formData.marketingChannels ?? []}
                    label={marketingChannelsLabel}
                    selectAllLabel={allMarketingLabel}
                    onChange={handleMarketingChannelsChange}
                    error={errors.marketingChannels}
                    disabled={isFormSubmitted}
                  />
                  <div className='m1-top'>
                    <Callout text={informationTextBox} />
                  </div>
                  <div className='form-nav'>
                    <ButtonContinue
                      name='btnSubmit'
                      text={t('general.submit')}
                      onClick={handleSubmit}
                      disabled={isFormSubmitted}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuppressionPage;
